import { SphereIconButton } from "@components/common/sphere-icon-button";
import { MemberGroupProjects } from "@components/common/member/member-group-projects";
import { ACTION_BTN_SIZE, ACTION_ICON_SIZE } from "@styles/common-styles";
import { SphereTooltip } from "@components/common/sphere-tooltip";
import { GroupId } from "@custom-types/base-types";
import { BaseMemberProps } from "@custom-types/member-types";
import { ArrowDownIcon, neutral, Text, ChevronUpIcon } from "@flat-ui";
import { Box, Grid2 } from "@mui/material";
import { CompanyMemberGroupEntity } from "@store/company-member-groups/company-member-groups-slice";
import { useAppSelector } from "@store/store-helper";
import { currentUserSelector } from "@store/user/user-selector";
import { withEllipsis } from "@styles/common-styles";
import { formatUserRoleType } from "@utils/data-display";
import {
  UserMenuEvents,
  UserProfile,
} from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { useState } from "react";
import { Link } from "react-router-dom";
import { generateGroupDetailsRoute } from "@router/router-helper";
import { useCompanyId } from "@context-providers/company-id-provider";

interface MemberGroupsTableProps extends BaseMemberProps {
  /** Company member groups */
  groups: CompanyMemberGroupEntity[];
}

/** Renders a table with the groups of a company member */
export function MemberGroupsTable({
  groups,
  member,
}: MemberGroupsTableProps): JSX.Element {
  const { trackEvent } = useTrackEvent();
  const [expandedGroups, setExpandedGroups] = useState<GroupId[]>([]);
  const currentUser = useAppSelector(currentUserSelector);
  return (
    <Box component="div">
      {groups.map((group) => (
        <Box component={"div"} key={group.id}>
          <MemberGroupRow
            group={group}
            member={member}
            expandedGroups={expandedGroups}
            setExpandedRows={(groups) => {
              trackEvent({
                name: UserProfile.expandGroupProjects,
                props: {
                  // Stores whether the action is done for displaying the current user
                  isCurrentUser: currentUser?.identity === member?.identity,
                },
              });
              setExpandedGroups(groups);
            }}
          />
          {expandedGroups.includes(group.id) && (
            <MemberGroupProjects group={group} member={member} />
          )}
        </Box>
      ))}
    </Box>
  );
}

interface MemberGroupRowProps extends BaseMemberProps {
  /** Group element*/
  group: CompanyMemberGroupEntity;

  /** Array of IDs of the expanded group */
  expandedGroups: GroupId[];

  /** Callback to set the expanded groups */
  setExpandedRows: React.Dispatch<React.SetStateAction<GroupId[]>>;
}

/** Padding on the side elements in the row for the member group */
const PADDING_SIDE = "12px";

/** Renders a single group row */
function MemberGroupRow({
  group,
  member,
  expandedGroups,
  setExpandedRows,
}: MemberGroupRowProps): JSX.Element {
  const companyId = useCompanyId();
  const isGroupExpanded = expandedGroups.includes(group.id);
  const { trackEvent } = useTrackEvent();

  function handleExpandGroup(event: React.MouseEvent<HTMLElement>): void {
    event.preventDefault();
    const isCurrentlyExpanded = expandedGroups.includes(group.id);
    setExpandedRows(isCurrentlyExpanded ? [] : [group.id]);
  }

  return (
    <Grid2
      container
      sx={{
        backgroundColor: neutral[50],
        mb: "8px",
        paddingY: "20px",
        "&:hover": {
          backgroundColor: neutral[100],
          cursor: "pointer",
        },
      }}
      onClick={handleExpandGroup}
    >
      {/* Group name */}
      <Grid2
        size={{ xs: 7, sm: 8 }}
        sx={{
          ...withEllipsis,
          paddingX: PADDING_SIDE,
        }}
      >
        <Link
          to={generateGroupDetailsRoute(companyId, group.id)}
          style={{ textDecoration: "none" }}
          onClick={(e) => {
            e.stopPropagation();
            trackEvent({
              name: UserMenuEvents.openGroupDetails,
              props: {
                origin: "Profile overview",
                kind: member.kind ?? "unknown",
                groupId: group.id,
              },
            });
          }}
        >
          <Text
            variant="bodyM"
            component="var"
            sx={{
              "&:hover": {
                textDecoration: "underline",
              },
            }}
          >
            {group.name}
          </Text>
        </Link>
      </Grid2>

      {/* Group role */}
      <Grid2
        size={{ xs: 4, sm: 3 }}
        sx={{
          ...withEllipsis,
          paddingRight: PADDING_SIDE,
        }}
      >
        <Text variant="bodyM">
          {group.role ? formatUserRoleType(group.role) : "Project Access"}
        </Text>
      </Grid2>

      {/* Button to expand/collapse group projects */}
      <Grid2
        size={{ xs: 1 }}
        sx={{
          paddingRight: PADDING_SIDE,
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
        }}
      >
        <SphereTooltip
          title={
            isGroupExpanded ? "Hide group projects" : "Show group projects"
          }
        >
          <SphereIconButton
            buttonSize={ACTION_BTN_SIZE}
            iconSize={ACTION_ICON_SIZE}
            component={isGroupExpanded ? ChevronUpIcon : ArrowDownIcon}
            onClick={handleExpandGroup}
          />
        </SphereTooltip>
      </Grid2>
    </Grid2>
  );
}
